import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    LinearProgress,
    Alert,
    Paper
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PercentIcon from '@mui/icons-material/Percent';
import AssignmentIcon from '@mui/icons-material/Assignment';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useNavigate } from 'react-router-dom';

interface BlockEvaluation {
    _id: string;
    title: string;
    blockId: {
        _id: string;
        title: string;
    };
    timeLimitMinutes: number;
    openPercentage: number;
    isLocked: boolean;
    completionPercentage: number;
    validatedCoursesCount: number;
    totalCoursesCount: number;
}

const LearnerBlockEvaluations: React.FC = () => {
    const [evaluations, setEvaluations] = useState<BlockEvaluation[]>([]);
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        fetchEvaluations();
    }, []);

    const fetchEvaluations = async () => {
        try {
            const res = await api.get('/api/block-evaluations/learner/available');
            setEvaluations(res.data);
        } catch (err) {
            console.error('Error fetching evaluations', err);
            showToast('Erreur lors du chargement des évaluations', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatTimeLimit = (minutes: number): string => {
        if (minutes < 60) return `${minutes} min`;
        if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60 > 0 ? minutes % 60 + 'min' : ''}`.trim();
        const days = Math.floor(minutes / 1440);
        const hours = Math.floor((minutes % 1440) / 60);
        return `${days}j ${hours > 0 ? hours + 'h' : ''}`.trim();
    };

    const handleEvaluationClick = (evaluation: BlockEvaluation) => {
        if (evaluation.isLocked) {
            showToast(`Cette évaluation nécessite ${evaluation.openPercentage}% de complétion du bloc`, 'warning');
            return;
        }
        // Navigate to evaluation page (to be implemented)
        navigate(`/learner/evaluations/${evaluation._id}`);
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
                <Typography>Chargement...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            p: { xs: 1, sm: 2, md: 4 },
            width: '100%',
            // force max-width to viewport width to prevent horizontal scroll
            maxWidth: { xs: '100vw', md: 1400 },
            boxSizing: 'border-box',
            mx: 'auto',
            overflowX: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{
                mb: 4,
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                textAlign: { xs: 'center', sm: 'left' }
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    gap: 2,
                    mb: 1
                }}>
                    <AssignmentIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
                    <Typography variant="h4" sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', sm: '2.125rem' }
                    }}>
                        Évaluations de Bloc
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ opacity: 0.9, fontFamily: 'Quicksand, sans-serif', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Complétez les blocs pour débloquer les évaluations
                </Typography>
            </Box>

            {evaluations.length === 0 ? (
                <Paper sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                    <AssignmentIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#64748b', fontFamily: 'Quicksand, sans-serif' }}>
                        Aucune évaluation disponible
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1, fontFamily: 'Quicksand, sans-serif' }}>
                        Les évaluations apparaîtront ici une fois configurées
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={{ xs: 2, md: 3 }}>
                    {evaluations.map((evaluation) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={evaluation._id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #f0f0f0',
                                    position: 'relative',
                                    overflow: 'visible',
                                    transition: 'all 0.3s ease',
                                    opacity: evaluation.isLocked ? 0.6 : 1,
                                    cursor: evaluation.isLocked ? 'not-allowed' : 'pointer',
                                    '&:hover': evaluation.isLocked ? {} : {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                                    }
                                }}
                                onClick={() => handleEvaluationClick(evaluation)}
                            >
                                {/* Lock Icon Overlay */}
                                {evaluation.isLocked && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        width: { xs: 32, sm: 60 },
                                        height: { xs: 32, sm: 60 },
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                                        zIndex: 10
                                    }}>
                                        <LockIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 30 } }} />
                                    </Box>
                                )}

                                {!evaluation.isLocked && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        width: { xs: 32, sm: 60 },
                                        height: { xs: 32, sm: 60 },
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                                        zIndex: 10
                                    }}>
                                        <LockOpenIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 30 } }} />
                                    </Box>
                                )}

                                <CardContent sx={{ p: 3 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            fontFamily: 'Quicksand, sans-serif',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere'
                                        }}
                                    >
                                        {evaluation.title}
                                    </Typography>

                                    {evaluation.blockId && (
                                        <Chip
                                            label={evaluation.blockId.title}
                                            size="small"
                                            sx={{
                                                mb: 2,
                                                background: 'rgba(103, 126, 234, 0.15)',
                                                color: '#667eea',
                                                fontWeight: 600,
                                                fontFamily: 'Quicksand, sans-serif',
                                                border: '1px solid rgba(103, 126, 234, 0.3)',
                                                maxWidth: '100%',
                                                '& .MuiChip-label': {
                                                    display: 'block',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }
                                            }}
                                        />
                                    )}

                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <AccessTimeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Quicksand, sans-serif' }}>
                                                Temps limite : {formatTimeLimit(evaluation.timeLimitMinutes)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PercentIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Quicksand, sans-serif' }}>
                                                Requis : {evaluation.openPercentage}% du bloc
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Progress Bar */}
                                    <Box sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                                                Votre progression ({evaluation.validatedCoursesCount}/{evaluation.totalCoursesCount} cours)
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: evaluation.completionPercentage >= evaluation.openPercentage ? '#10b981' : '#ef4444',
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {evaluation.completionPercentage}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={evaluation.completionPercentage}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                bgcolor: '#e2e8f0',
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: evaluation.completionPercentage >= evaluation.openPercentage ? '#10b981' : '#ef4444',
                                                    borderRadius: 4
                                                }
                                            }}
                                        />
                                        {evaluation.isLocked && (
                                            <Alert severity="warning" sx={{ mt: 2, fontFamily: 'Quicksand, sans-serif' }}>
                                                <Typography variant="caption">
                                                    Complétez {evaluation.openPercentage - evaluation.completionPercentage}% de plus pour débloquer
                                                </Typography>
                                            </Alert>
                                        )}
                                        {!evaluation.isLocked && (
                                            <Alert severity="success" sx={{ mt: 2, fontFamily: 'Quicksand, sans-serif' }}>
                                                <Typography variant="caption">
                                                    ✓ Évaluation débloquée !
                                                </Typography>
                                            </Alert>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default LearnerBlockEvaluations;
